import { ANALYTICS_CONFIG } from './config'
import { hasAnalyticsConsent, LEGAL_CONSENT_EVENT } from '@/legal/consent'

const VISITOR_KEY = 'druzhba_analytics_visitor_v1'
const SESSION_KEY = 'druzhba_analytics_session_v1'
const QUEUE_KEY = 'druzhba_analytics_queue_v1'
const ATTRIBUTION_KEY = 'druzhba_analytics_attribution_v1'
const SESSION_TIMEOUT = 30 * 60 * 1000
const FLUSH_INTERVAL = 5000
const MAX_QUEUE = 250
const PRIVATE_KEYS = /email|phone|name|fio|message|comment|address|password|token|hash|secret|card|recipient/i

let state = null
let queue = []
let flushTimer = null
let sending = false
let installed = false

function storageGet(storage, key) {
  try { return storage.getItem(key) } catch (_) { return null }
}
function storageSet(storage, key, value) {
  try { storage.setItem(key, value); return true } catch (_) { return false }
}
function parseJson(value, fallback) {
  try { return JSON.parse(String(value || '')) } catch (_) { return fallback }
}
function uuid() {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID()
  const bytes = new Uint8Array(16)
  if (window.crypto && window.crypto.getRandomValues) window.crypto.getRandomValues(bytes)
  else for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256)
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0,8)}-${hex.slice(8,12)}-${hex.slice(12,16)}-${hex.slice(16,20)}-${hex.slice(20)}`
}
function safeString(value, max = 240) { return String(value == null ? '' : value).replace(/[\u0000-\u001f]/g, ' ').trim().slice(0, max) }
function sanitize(value, depth = 0) {
  if (depth > 4) return undefined
  if (value === null || value === undefined) return value
  if (typeof value === 'boolean' || typeof value === 'number') return Number.isFinite(value) ? value : undefined
  if (typeof value === 'string') return safeString(value, 240)
  if (Array.isArray(value)) return value.slice(0, 20).map(v => sanitize(v, depth + 1)).filter(v => v !== undefined)
  if (typeof value === 'object') {
    return Object.keys(value).slice(0, 40).reduce((acc, key) => {
      if (PRIVATE_KEYS.test(key)) return acc
      const next = sanitize(value[key], depth + 1)
      if (next !== undefined) acc[key] = next
      return acc
    }, {})
  }
  return safeString(value)
}
function parseUa() {
  const ua = navigator.userAgent || ''
  const browser = /Edg\//.test(ua) ? 'Edge' : /OPR\//.test(ua) ? 'Opera' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : 'Other'
  const os = /Windows NT/.test(ua) ? 'Windows' : /Android/.test(ua) ? 'Android' : /iPhone|iPad|iPod/.test(ua) ? 'iOS' : /Mac OS X/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : 'Other'
  return { browser, os }
}
function deviceType() {
  const width = window.innerWidth || screen.width || 0
  if (width <= 680) return 'mobile'
  if (width <= 1180) return 'tablet'
  return 'desktop'
}
function queryParam(name) {
  try { return new URL(window.location.href).searchParams.get(name) || '' } catch (_) { return '' }
}
function classifySource(referrerHost, utmSource, utmMedium) {
  if (utmSource) return safeString(utmSource, 100).toLowerCase()
  const host = String(referrerHost || '').toLowerCase()
  if (!host) return 'direct'
  if (host.includes('yandex.')) return 'yandex'
  if (host.includes('google.')) return 'google'
  if (host.includes('vk.com') || host.includes('vk.ru')) return 'vk'
  if (host.includes('t.me') || host.includes('telegram')) return 'telegram'
  if (host.includes('dzen.')) return 'dzen'
  return host.replace(/^www\./, '').slice(0, 100)
}
function attribution() {
  const saved = parseJson(storageGet(localStorage, ATTRIBUTION_KEY), null)
  const hasCampaignSignal = ['utm_source','utm_medium','utm_campaign','utm_content','utm_term','yclid','vk_click_id','vk_click'].some(queryParam)
  if (saved && !hasCampaignSignal) return saved
  let referrerHost = ''
  try { referrerHost = document.referrer ? new URL(document.referrer).hostname : '' } catch (_) {}
  const data = {
    source: classifySource(referrerHost, queryParam('utm_source'), queryParam('utm_medium')),
    medium: safeString(queryParam('utm_medium') || (referrerHost ? 'referral' : 'none'), 100),
    campaign: safeString(queryParam('utm_campaign'), 180),
    content: safeString(queryParam('utm_content'), 180),
    term: safeString(queryParam('utm_term'), 180),
    yclid: safeString(queryParam('yclid') || (saved && saved.yclid) || '', 180),
    vk_click_id: safeString(queryParam('vk_click_id') || queryParam('vk_click') || (saved && saved.vk_click_id) || '', 180),
    referrer_host: safeString(referrerHost, 180),
    landing_path: safeString(window.location.pathname + window.location.search, 500),
    captured_at: Date.now()
  }
  storageSet(localStorage, ATTRIBUTION_KEY, JSON.stringify(data))
  return data
}
function makeState() {
  const now = Date.now()
  let visitor = parseJson(storageGet(localStorage, VISITOR_KEY), null)
  const isNewVisitor = !visitor || !visitor.id
  if (isNewVisitor) visitor = { id: uuid(), created_at: now }
  visitor.last_seen_at = now
  storageSet(localStorage, VISITOR_KEY, JSON.stringify(visitor))

  let session = parseJson(storageGet(sessionStorage, SESSION_KEY), null)
  if (!session || !session.id || now - Number(session.last_activity || 0) > SESSION_TIMEOUT) {
    session = { id: uuid(), started_at: now, last_activity: now }
  } else session.last_activity = now
  storageSet(sessionStorage, SESSION_KEY, JSON.stringify(session))

  return { visitor, session, isNewVisitor, attribution: attribution(), ...parseUa() }
}
function sessionPayload() {
  if (!state) state = makeState()
  const tz = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || '' } catch (_) { return '' } })()
  return {
    started_at: new Date(state.session.started_at).toISOString(),
    last_seen_at: new Date().toISOString(),
    landing_path: state.attribution.landing_path,
    exit_path: safeString(window.location.pathname + window.location.search, 500),
    referrer_host: state.attribution.referrer_host,
    source: state.attribution.source,
    medium: state.attribution.medium,
    campaign: state.attribution.campaign,
    content: state.attribution.content,
    term: state.attribution.term,
    device_type: deviceType(),
    browser: state.browser,
    os: state.os,
    screen_width: Number(screen.width || window.innerWidth || 0),
    screen_height: Number(screen.height || window.innerHeight || 0),
    language: safeString(navigator.language || '', 20),
    timezone: safeString(tz, 80),
    is_new_visitor: state.isNewVisitor,
    auth_state: storageGet(localStorage, 'user_hash') ? 'authorized' : 'guest'
  }
}
function loadQueue() {
  queue = parseJson(storageGet(localStorage, QUEUE_KEY), [])
  if (!Array.isArray(queue)) queue = []
  queue = queue.slice(-MAX_QUEUE)
}
function persistQueue() { storageSet(localStorage, QUEUE_KEY, JSON.stringify(queue.slice(-MAX_QUEUE))) }
function eventBase(name, data = {}) {
  if (!state) state = makeState()
  state.session.last_activity = Date.now()
  storageSet(sessionStorage, SESSION_KEY, JSON.stringify(state.session))
  return {
    event_id: uuid(),
    event_name: safeString(name, 80),
    event_category: safeString(data.category || 'internal', 80),
    priority: safeString(data.priority || 'micro', 20),
    event_label: safeString(data.label || '', 180),
    page_path: safeString(data.path || window.location.pathname + window.location.search, 500),
    page_title: safeString(data.title || document.title, 250),
    product_id: safeString(data.product_id || '', 80),
    product_name: safeString(data.product_name || '', 180),
    order_id: safeString(data.order_id || '', 80),
    value: Number(data.value || 0) || 0,
    currency: safeString(data.currency || ANALYTICS_CONFIG.currency || 'RUB', 3),
    meta: sanitize(data.meta || {}),
    occurred_at: new Date().toISOString()
  }
}
export function recordInternalEvent(name, data = {}) {
  if (!ANALYTICS_CONFIG.internalEnabled || !name || !hasAnalyticsConsent()) return false
  queue.push(eventBase(name, data))
  if (queue.length > MAX_QUEUE) queue.splice(0, queue.length - MAX_QUEUE)
  persistQueue()
  if (queue.length >= 15) flushInternalEvents()
  return true
}
export function recordGoalInternal(goal, params = {}) {
  const product = params && params.product || {}
  const order = params && params.order || {}
  return recordInternalEvent(goal.id, {
    category: goal.category,
    priority: goal.priority,
    label: goal.name,
    product_id: product.id,
    product_name: product.name,
    order_id: order.id || params.order_id,
    value: params.order_price || params.value || 0,
    currency: params.currency || 'RUB',
    meta: params
  })
}
export function recordPageView(route, extra = {}) {
  return recordInternalEvent('page_view', {
    category: 'system',
    priority: 'system',
    path: route && route.fullPath || window.location.pathname + window.location.search,
    title: document.title,
    meta: { route_name: route && route.name || '', ...extra }
  })
}
export function recordEcommerce(action, product = {}, actionField = {}) {
  return recordInternalEvent(`ecommerce_${action}`, {
    category: 'ecommerce',
    priority: action === 'purchase' ? 'macro' : 'micro',
    product_id: product.id,
    product_name: product.name,
    order_id: actionField.id || actionField.order_id,
    value: action === 'purchase' ? Number(actionField.revenue || actionField.value || 0) : Number(product.price || 0) * Number(product.quantity || 1),
    currency: 'RUB',
    meta: { product, action_field: actionField }
  })
}
async function sendPayload(events, keepalive = false) {
  const payload = {
    visitor_id: state.visitor.id,
    session_id: state.session.id,
    sent_at: new Date().toISOString(),
    session: sessionPayload(),
    events
  }
  const response = await fetch(ANALYTICS_CONFIG.internalEndpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    keepalive
  })
  if (!response.ok) throw new Error(`analytics_http_${response.status}`)
  const body = await response.json().catch(() => ({}))
  if (String(body.status || '') !== '200') throw new Error('analytics_rejected')
}
export async function flushInternalEvents(keepalive = false) {
  if (!ANALYTICS_CONFIG.internalEnabled || !hasAnalyticsConsent() || sending || !queue.length || !navigator.onLine) return false
  sending = true
  const batch = queue.slice(0, 50)
  try {
    await sendPayload(batch, keepalive)
    const ids = new Set(batch.map(item => item.event_id))
    queue = queue.filter(item => !ids.has(item.event_id))
    persistQueue()
    return true
  } catch (error) {
    if (ANALYTICS_CONFIG.metricaDebug) console.warn('[INTERNAL_ANALYTICS]', error.message || error)
    return false
  } finally { sending = false }
}
function observeWebVitals() {
  if (!('PerformanceObserver' in window)) return
  const values = {}
  const send = () => {
    if (!Object.keys(values).length) return
    recordInternalEvent('web_vitals', { category: 'performance', priority: 'diagnostic', meta: { ...values } })
  }
  try {
    const lcp = new PerformanceObserver(list => { const entries=list.getEntries(); const last=entries[entries.length-1]; if(last) values.lcp=Math.round(last.startTime) })
    lcp.observe({ type: 'largest-contentful-paint', buffered: true })
  } catch (_) {}
  try {
    let cls=0
    const obs = new PerformanceObserver(list => list.getEntries().forEach(entry => { if(!entry.hadRecentInput) cls += entry.value }))
    obs.observe({ type: 'layout-shift', buffered: true })
    window.addEventListener('pagehide', () => { values.cls = Number(cls.toFixed(4)); send() }, { once: true })
  } catch (_) {}
  try {
    const nav = performance.getEntriesByType('navigation')[0]
    if (nav) values.dom_ready = Math.round(nav.domContentLoadedEventEnd)
  } catch (_) {}
  window.setTimeout(send, 10000)
}
export function installInternalAnalytics() {
  if (installed) return
  if (!hasAnalyticsConsent()) {
    const activate = () => { if (hasAnalyticsConsent()) { window.removeEventListener(LEGAL_CONSENT_EVENT, activate); installInternalAnalytics() } }
    window.addEventListener(LEGAL_CONSENT_EVENT, activate)
    return
  }
  installed = true
  state = makeState()
  loadQueue()
  recordInternalEvent('session_start', { category: 'system', priority: 'system', meta: { new_visitor: state.isNewVisitor } })
  flushTimer = window.setInterval(() => flushInternalEvents(false), FLUSH_INTERVAL)
  window.addEventListener('online', () => flushInternalEvents(false))
  window.addEventListener('pagehide', () => {
    recordInternalEvent('page_leave', { category: 'system', priority: 'system', meta: { duration_seconds: Math.round((Date.now() - state.session.started_at) / 1000) } })
    flushInternalEvents(true)
  })
  document.addEventListener('visibilitychange', () => { if (document.hidden) flushInternalEvents(true) })
  observeWebVitals()
  window.__DRUZHBA_ANALYTICS__ = {
    installed: true,
    visitorId: state.visitor.id,
    sessionId: state.session.id,
    endpoint: ANALYTICS_CONFIG.internalEndpoint,
    flush: () => flushInternalEvents(false),
    queueSize: () => queue.length,
    record: (name, data) => recordInternalEvent(name, data)
  }
}
